import React, { useEffect, useState } from "react";
import "./SyncSettingsPanel.css";
import {
  getSyncSettings,
  listOutlineCollections,
  OutlineCollection,
  setOutlineCollection,
  setPrimaryAdapter,
  SyncSettings,
  SyncAdapter,
} from "../utils/api";

const SyncSettingsPanel: React.FC = () => {
  const [primaryAdapter, setPrimaryAdapterState] = useState<SyncAdapter>("outline");
  const [collections, setCollections] = useState<OutlineCollection[]>([]);
  const [selectedCollectionId, setSelectedCollectionId] = useState<string>("");
  const [loadingCollections, setLoadingCollections] = useState(true);
  const [loadingSettings, setLoadingSettings] = useState(true);
  const [savingAdapter, setSavingAdapter] = useState(false);
  const [savingCollection, setSavingCollection] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [settingsSnapshot, setSettingsSnapshot] = useState<SyncSettings | null>(null);

  useEffect(() => {
    void loadSettings();
    void loadCollections();
  }, []);

  const loadSettings = async () => {
    setLoadingSettings(true);
    try {
      const settings = await getSyncSettings();
      setSettingsSnapshot(settings);
      setPrimaryAdapterState(settings.primaryAdapter);
      if (settings.outline.collectionId) {
        setSelectedCollectionId(settings.outline.collectionId);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load sync settings");
    } finally {
      setLoadingSettings(false);
    }
  };

  const loadCollections = async () => {
    setLoadingCollections(true);
    setError(null);
    try {
      const nextCollections = await listOutlineCollections();
      setCollections(nextCollections);
      if (nextCollections.length > 0) {
        setSelectedCollectionId((current) => current || nextCollections[0].id);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load Outline collections");
    } finally {
      setLoadingCollections(false);
    }
  };

  const handleSaveAdapter = async () => {
    setSavingAdapter(true);
    setMessage(null);
    setError(null);
    try {
      await setPrimaryAdapter(primaryAdapter);
      await loadSettings();
      setMessage("Primary adapter saved.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save primary adapter");
    } finally {
      setSavingAdapter(false);
    }
  };

  const handleSaveCollection = async () => {
    if (!selectedCollectionId) {
      return;
    }
    setSavingCollection(true);
    setMessage(null);
    setError(null);
    try {
      await setOutlineCollection(selectedCollectionId);
      await loadSettings();
      setMessage("Outline collection saved.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save Outline collection");
    } finally {
      setSavingCollection(false);
    }
  };

  return (
    <section className="sync-settings-panel">
      <h3>Sync Settings</h3>

      <div className="sync-settings-group">
        <label>Primary adapter</label>
        <div className="sync-settings-inline">
          <select
            value={primaryAdapter}
            onChange={(e) => setPrimaryAdapterState(e.target.value as SyncAdapter)}
          >
            <option value="outline">Outline</option>
          </select>
          <button type="button" onClick={handleSaveAdapter} disabled={savingAdapter}>
            {savingAdapter ? "Saving..." : "Save"}
          </button>
        </div>
      </div>

      <div className="sync-settings-group">
        <label>Outline collection lock</label>
        <div className="sync-settings-inline">
          <select
            value={selectedCollectionId}
            onChange={(e) => setSelectedCollectionId(e.target.value)}
            disabled={loadingCollections || collections.length === 0}
          >
            {collections.length === 0 && <option value="">No collections available</option>}
            {collections.map((collection) => (
              <option key={collection.id} value={collection.id}>
                {collection.name}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={handleSaveCollection}
            disabled={savingCollection || !selectedCollectionId}
          >
            {savingCollection ? "Saving..." : "Save"}
          </button>
        </div>
      </div>

      {!loadingSettings && (
        <div className="sync-settings-group sync-settings-status">
          <label>Outline connection status</label>
          <p>
            {settingsSnapshot?.outline.ready
              ? "Ready for sync"
              : `Not ready${settingsSnapshot?.outline.reason ? `: ${settingsSnapshot.outline.reason}` : ""}`}
          </p>
          {!!selectedCollectionId && (
            <p className="sync-settings-selected">
              Selected collection: <strong>{collections.find((c) => c.id === selectedCollectionId)?.name || selectedCollectionId}</strong>
            </p>
          )}
          {!selectedCollectionId && (
            <p className="sync-settings-remediation">
              Remediation: select an Outline collection and save to unlock Outline sync.
            </p>
          )}
        </div>
      )}

      {message && <p className="sync-settings-message">{message}</p>}
      {error && <p className="sync-settings-error">{error}</p>}
    </section>
  );
};

export default SyncSettingsPanel;
